import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import PlaceIcon from '@mui/icons-material/Place';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const statusColors = {
  info: '#2196f3',
  clear: '#4caf50',
  warning: '#ff9800',
  emergency: '#f44336',
  incomplete: '#9e9e9e',
};

const CheckpointStatsCard = ({ startDate, endDate, stats = {} }) => {
  const theme = useTheme();

  const data = {
    total: stats.total || 0,
    info: stats.info || 0,
    clear: stats.clear || 0,
    warning: stats.warning || 0,
    emergency: stats.emergency || 0,
    incomplete: stats.incomplete || 0,
  };

  const pieData = [
    { id: 0, value: data.info, label: 'Info', color: statusColors.info },
    { id: 1, value: data.clear, label: 'Clear', color: statusColors.clear },
    { id: 2, value: data.warning, label: 'Warning', color: statusColors.warning },
    { id: 3, value: data.emergency, label: 'Emergency', color: statusColors.emergency },
    { id: 4, value: data.incomplete, label: 'Incomplete', color: statusColors.incomplete },
  ].filter(item => item.value > 0);

  const clearPercent = data.total > 0 ? Math.round((data.clear / data.total) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        height: '100%',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PlaceIcon sx={{ color: 'secondary.main', fontSize: 24 }} />
            <Typography variant="h6" fontWeight={600}>
              Checkpoint Stats
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {startDate} - {endDate}
          </Typography>
        </Box>
        <InfoOutlinedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
      </Box>

      {/* Chart with center label */}
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, position: 'relative' }}>
        {pieData.length > 0 ? (
          <>
            <PieChart
              series={[
                {
                  data: pieData,
                  innerRadius: 50,
                  outerRadius: 68,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  cx: 70,
                  cy: 70,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 45, additionalRadius: -5, color: 'gray' },
                },
              ]}
              width={140}
              height={140}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              slotProps={{
                legend: { hidden: true },
              }}
            />
            {/* Center percentage overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="h5" fontWeight={700} color="success.main">
                {clearPercent}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Clear Rate
              </Typography>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              width: 140,
              height: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.divider, 0.1),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No data
            </Typography>
          </Box>
        )}
      </Box>

      {/* Stats Summary */}
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {data.total.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Checks
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" fontWeight={700}>
              {data.info}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Info Checks
            </Typography>
          </Box>
        </Box>

        {/* Status Legend */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip
            size="small"
            label={`Clear: ${data.clear.toLocaleString()}`}
            sx={{
              bgcolor: alpha(statusColors.clear, 0.15),
              color: statusColors.clear,
              fontWeight: 600,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
          <Chip
            size="small"
            label={`Warning: ${data.warning}`}
            sx={{
              bgcolor: alpha(statusColors.warning, 0.15),
              color: statusColors.warning,
              fontWeight: 600,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
          <Chip
            size="small"
            label={`Emergency: ${data.emergency}`}
            sx={{
              bgcolor: alpha(statusColors.emergency, 0.15),
              color: statusColors.emergency,
              fontWeight: 600,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        </Stack>
      </Box>
    </Paper>
  );
};

export default CheckpointStatsCard;
